import { events } from '@/app/events/events';
import type {
  CoreClientImportProgress,
  CoreClientTransactionStart,
} from '@/app/web-workers/quadraticCore/coreClientMessages';
import { filesImportProgressListAtom } from '@/dashboard/atoms/filesImportProgressListAtom';
import { atom, getDefaultStore } from 'jotai';

export interface FileImportProgress {
  name: string;
  size: number;
  step: 'read' | 'create' | 'save' | 'done' | 'error' | 'cancel';
  progress: number;
  transactionId?: string;
  transactionOps?: number;
  uuid?: string;
  abortController?: AbortController;
}

interface FilesImportProgressState {
  importing: boolean;
  createNewFile: boolean;
  currentFileIndex?: number;
  files: FileImportProgress[];
}

const defaultFilesImportProgressState: FilesImportProgressState = {
  importing: false,
  createNewFile: false,
  currentFileIndex: undefined,
  files: [],
};

export const filesImportProgressAtom = atom(defaultFilesImportProgressState);

const store = getDefaultStore();

const handleImportProgress = (message: CoreClientImportProgress) => {
  store.set(filesImportProgressAtom, (prev) => {
    const updatedFiles = prev.files.map((file, index) => {
      if (index !== prev.currentFileIndex) return file;
      const totalSteps = prev.createNewFile ? 2 : 1;
      const newFile: FileImportProgress = {
        ...file,
        step: 'read',
        progress: Math.round((message.current / message.total) * 100) / totalSteps,
      };
      return newFile;
    });
    return {
      ...prev,
      files: updatedFiles,
    };
  });
};

const handleTransactionStart = (message: CoreClientTransactionStart) => {
  if (message.transactionName === 'Import') {
    store.set(filesImportProgressAtom, (prev) => {
      const updatedFiles = prev.files.map((file, index) => {
        if (index !== prev.currentFileIndex) return file;
        const totalSteps = prev.createNewFile ? 2 : 1;
        const newFile: FileImportProgress = {
          ...file,
          step: 'create',
          progress: 100 / totalSteps,
          transactionId: message.transactionId,
          transactionOps: undefined,
        } as FileImportProgress;
        return newFile;
      });
      return {
        ...prev,
        files: updatedFiles,
      };
    });
  }
};

let previousState: FilesImportProgressState = defaultFilesImportProgressState;
store.sub(filesImportProgressAtom, () => {
  const newValue = store.get(filesImportProgressAtom);
  const oldValue = previousState;
  previousState = newValue;

  if (!oldValue.importing && newValue.importing) {
    events.on('importProgress', handleImportProgress);
    events.on('transactionStart', handleTransactionStart);
  } else if (oldValue.importing && !newValue.importing) {
    events.off('importProgress', handleImportProgress);
    events.off('transactionStart', handleTransactionStart);
    const filesImportProgressListState = store.get(filesImportProgressListAtom);
    if (!filesImportProgressListState.show) {
      store.set(filesImportProgressAtom, defaultFilesImportProgressState);
    }
  }
});
